import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { api, fmtUSD, fmtUSD2, monthLabel } from "@/lib/api";
import { PRODUCT_TYPES, PRODUCT_COLORS, productTypeShort } from "@/lib/constants";

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="rounded-md border border-border bg-card p-3 text-xs shadow-md">
      <div className="mb-1.5 font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      {payload.filter((p) => p.value > 0).map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: p.color }} />{productTypeShort(p.dataKey)}</span>
          <span className="font-mono">{fmtUSD2(p.value)}</span>
        </div>
      ))}
      <div className="mt-1.5 flex items-center justify-between border-t border-border pt-1.5 font-mono text-[11px]"><span>Total</span><span>{fmtUSD2(total)}</span></div>
    </div>
  );
}

export default function RevenueCharts({ year = new Date().getFullYear() }) {
  const [mode, setMode] = useState("booked"); // booked | paid
  const { data, isLoading } = useQuery({
    queryKey: ["crm-revenue", year],
    queryFn: async () => (await api.get(`/crm/revenue?year=${year}`)).data,
  });

  const chartData = useMemo(() => {
    if (!data) return [];
    const group = data[mode]?.perMonth || {};
    return Object.keys(group).sort().map((ym) => {
      const row = { month: monthLabel(ym).replace(/\./g, "") };
      for (const pt of PRODUCT_TYPES.map((p) => p.id)) {
        row[pt] = Number((group[ym].byType?.[pt] || 0).toFixed(2));
      }
      return row;
    });
  }, [data, mode]);

  const yearTotal = data?.[mode]?.total || 0;
  const byType = data?.[mode]?.byType || {};

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="revenue-charts">
      <div className="flex flex-col gap-3 border-b border-border pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="kicker">Revenue {year}</div>
          <div className="mt-1 font-display text-4xl leading-none">{fmtUSD(yearTotal)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {mode === "booked" ? "Suma de cotizaciones aceptadas por mes" : "Suma de facturas pagadas por mes"}
          </div>
        </div>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList>
            <TabsTrigger value="booked" data-testid="revenue-booked-tab">Reservado</TabsTrigger>
            <TabsTrigger value="paid" data-testid="revenue-paid-tab">Pagado</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="mt-5 h-[280px] w-full" data-testid="revenue-chart">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" formatter={(v) => productTypeShort(v)} />
              {PRODUCT_TYPES.map((pt) => (
                <Bar key={pt.id} dataKey={pt.id} stackId="revenue" fill={PRODUCT_COLORS[pt.id]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-6">
        {PRODUCT_TYPES.map((pt) => (
          <div key={pt.id} className="rounded-md border border-border bg-background px-3 py-2" data-testid={`revenue-type-${pt.id}`}>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="inline-block h-2 w-2 rounded-sm" style={{ background: PRODUCT_COLORS[pt.id] }} />
              {pt.short}
            </div>
            <div className="mt-1 font-mono text-sm">{fmtUSD2(byType[pt.id] || 0)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
